/**
 * The member identity screens' copy and routes — `MEM-005` … `MEM-010`.
 *
 * This module imports nothing but `@h2hspark/contracts` — no `@/` alias, no React, no API client
 * — so the test runner can load it without a bundler. That is the same constraint every other
 * slice's `constants.ts` carries, and it is what makes a screen's wording assertable in a repo
 * with no DOM renderer: copy an acceptance criterion quotes has to be a value, not JSX.
 *
 * The copy itself lives in `contracts` rather than here, because the API serves some of it (the
 * privacy screen's election definitions, the opt-out statement) and both halves must be the same
 * strings. What is added here is what only the client knows: where each screen lives.
 */
import {
  emailAddressField,
  memberProfileServiceNeedsProfile,
  type MemberProfileServiceMethod,
} from '@h2hspark/contracts';

export {
  MEMBER_ACCOUNT_COPY,
  MEMBER_VERIFY_EMAIL_COPY,
  MEMBER_VERIFY_EMAIL_CODE_PARAM,
  MEMBER_VERIFY_EMAIL_TOKEN_PARAM,
  MEMBER_MATCHING_PREFERENCES_COPY,
  MEMBER_PREFERENCES_APPLY_NOTICE,
  MEMBER_PRIVACY_COPY,
  MEMBER_SOCIAL_CHANNELS_COPY,
} from '@h2hspark/contracts';

/**
 * Where the member identity screens live.
 *
 * **All of them hang off `/app/account`**, which is itself the **You** destination — the desktop
 * avatar's and the phone tab bar's — since 2026-09-12. `Web App.dc.html` §2.15's Account
 * Management card is the hub for the rest, and there is no second identity screen above them.
 *
 * **`/app/profile` and `/app/profile/events` are gone, with no redirect.** Sumit, reviewing
 * Phase 5: *"profile is still taking to old profile page"* and *"we don't need to redirect the old
 * profile url, it can just be removed."* Ruling 38 is overturned; `MEM-005`'s five criteria are
 * struck to `PLT-013`, and `docs/plans/rulings-2026-09-11.md` carries the new ruling. Nothing here
 * names the old address, which is what makes the removal complete rather than cosmetic.
 */
export const MEMBER_PROFILE_ROUTES = {
  /**
   * Where a member lands after signing out (`USR-007` AC-3).
   *
   * Named here rather than written into `my-account-page.tsx` because it is the same address
   * `auth/public-paths.ts` exempts and `UnauthenticatedRedirector` sends an expired session to —
   * three references to one route, and the one that goes stale silently is the one typed inline.
   */
  signIn: '/app/login',
  account: '/app/account',
  socialChannels: '/app/account/social-channels',
  matchingPreferences: '/app/account/matching-preferences',
  privacy: '/app/account/privacy',
  /**
   * `Web App.dc.html` §2.15's *Change password* row, which in the prototype has no destination at
   * all — the design's own gap, logged as `handoff-corrections.md` #129.
   *
   * It was an inline card at `/app/account#password` until Phase 5. A page rather than an anchor
   * because the merged Your account screen is now a two-column layout with a profile form in it,
   * and a password form folded into that column would put two unrelated Saves on one screen — the
   * thing `MEM-007` AC-4's single pinned footer exists to prevent. The anchor is kept working by
   * the route below redirecting, because `member-app-parity.md` §6 and two mailed links point at
   * the old address.
   */
  changePassword: '/app/account/password',
  confirmEmail: '/app/account/confirm-email',
  /**
   * `USR-003` AC-5's Verify your email screen.
   *
   * **Under `account/` rather than at `/app/verify-email`**, and beside `confirmEmail` rather than
   * folded into it. The two look alike and are not the same act: `confirmEmail` finishes moving an
   * account to a *new* address (`MEM-007` AC-2, our own token), and this one proves the address the
   * account already has (`USR-003`, the identity provider's code). One screen doing both would
   * have to branch on which credential it was holding, and the branch a member landed on would
   * depend on which email they happened to open.
   */
  verifyEmail: '/app/account/verify-email',
  /**
   * `MEM-022` AC-3's destination, reached from the account page.
   *
   * Declared here beside its siblings so the account row and the route table cannot disagree, and
   * mirrored by `TICKETS_AND_ORDERS_PATH` in `event-orders/constants.ts` — which is the slice that
   * owns the screen. The two are asserted equal rather than one importing the other, because a
   * members-domain import of an event-orders constant would make the account page depend on the
   * orders slice to draw a row.
   */
  ticketsAndOrders: '/app/account/orders',
} as const;

/**
 * Where the **You** tab sends an account that has no member profile yet (`USR-014` AC-7).
 *
 * `USR-003`'s wizard, at `/app/join` — **the same wizard** the reserve bar sends the same account
 * to, carrying a `returnTo` so the person lands back where they set out from. AC-2's "rather than
 * a second onboarding" is a statement about this address: there is one wizard, and every door to
 * it is this one route with a different `returnTo`.
 *
 * The `returnTo` is **Your account** since 2026-09-12, because that is now what **You** opens.
 * `/app/profile` was the destination and is gone; a `returnTo` pointing at a removed screen would
 * land somebody who had just finished the wizard on the marketing home, which is where the
 * router's `*` catch-all sends every address nothing claims.
 *
 * Spelled here rather than inline so the account screen and `event-orders`'
 * `joinThenCheckoutPath` are visibly two callers of one contract, and `encodeURIComponent` for the
 * reason that helper uses it — a destination goes in a query parameter and is encoded, always,
 * even when today's value happens to contain nothing that needs it.
 */
export const MEMBER_PROFILE_SETUP_PATH = `/app/join?returnTo=${encodeURIComponent(
  MEMBER_PROFILE_ROUTES.account,
)}`;

/* ============================================================================
   What My Account may offer an account with no member profile (`MEM-049`)
   ============================================================================ */

/**
 * **Every control on My Account, and the `MemberProfileService` methods its own request calls.**
 *
 * This is the screen's half of `MEM-049` AC-3, and it is deliberately *not* an answer to "does
 * this need a profile?". That question is answered once, in `@h2hspark/contracts`'
 * {@link MEMBER_PROFILE_SERVICE_NEEDS_PROFILE}, which is derived from `profile-service.ts` by a
 * test that fails the build when a method gains or loses `mustFind`. What this table states is the
 * one fact only the screen knows: **which call is behind which control.** The two are composed by
 * {@link memberAccountControlWorksWithoutProfile}, so the screen holds no copy of the answer and
 * therefore cannot hold a stale one.
 *
 * That distinction is the whole story. `/app/account` was fixed twice by adding a condition to the
 * screen, each fix correct about the control in front of it, and a third report followed — because
 * a condition on a screen is a restatement, and restatements go out of date. A method that starts
 * or stops needing a profile now moves every control that calls it, in one edit, in `contracts`.
 *
 * ## The empty arrays are claims, not gaps
 *
 * A control with `[]` calls **no** `MemberProfileService` method at all, and each one is a
 * different endpoint:
 *
 *   - `password` — `packages/auth-ui`'s `ChangePasswordForm`, against the identity provider. It is
 *     an anchor on this same page rather than a destination.
 *   - `tickets` — `EventOrdersService.listMyOrders` behind `GET /api/event-orders/mine`, which
 *     reads orders and never opens the registration row. `MEM-049`'s *What AC-1 does not mean* is
 *     about this row in as many words: hiding it "would withhold something a member can use, which
 *     is the mirror of the defect and no better".
 *   - `sign-out` — `POST /auth/logout`. The member app draws this control here and nowhere else
 *     (`USR-007` AC-3), so hiding it would leave an unfinished account no way out of the app.
 *
 * ## Why arrays rather than one method each
 *
 * A card is its reads *and* its writes. The photo card uploads and removes; the preferences screen
 * reads and saves. A control is offered only when **every** call behind it can succeed, so the
 * predicate is an `any` over the list — a card whose read works and whose Save 404s is the exact
 * shape of the first fix's defect, which offered the profile form and left Save answering 404.
 */
export const MEMBER_ACCOUNT_CONTROL_READS = {
  photo: ['readPhotoObjectKey', 'attachPhoto', 'detachPhoto'],
  identity: ['saveAccount'],
  email: ['requestEmailChange'],
  /** Its own card, and part of the same `PUT /members/me/account` as the identity fields. */
  biography: ['saveAccount'],
  password: [],
  'social-channels': ['readSocialChannels', 'saveSocialChannels'],
  'matching-preferences': ['readMatchingPreferences', 'saveMatchingPreferences'],
  privacy: ['readPrivacyElections', 'savePrivacyElections'],
  tickets: [],
  deletion: ['readDeletionRequest', 'requestAccountDeletion', 'cancelAccountDeletionRequest'],
  'sign-out': [],
} as const satisfies Readonly<Record<string, ReadonlyArray<MemberProfileServiceMethod>>>;

/** A control on My Account, by name. Typed, so a settings row cannot name one that is undeclared. */
export type MemberAccountControlId = keyof typeof MEMBER_ACCOUNT_CONTROL_READS;

/**
 * Can this control succeed for a member who has not answered the profile questions? (`MEM-049`
 * AC-1, AC-2.)
 *
 * The rule the story states exactly: a control goes when **its own request** needs a profile. Not
 * when it looks related to one, and not when it might fail for some other reason.
 */
export function memberAccountControlWorksWithoutProfile(id: MemberAccountControlId): boolean {
  const reads: ReadonlyArray<MemberProfileServiceMethod> = MEMBER_ACCOUNT_CONTROL_READS[id];
  return !reads.some((method) => memberProfileServiceNeedsProfile(method));
}

/**
 * The prototype's **Security & connections** card — five rows, each a tinted icon tile, a label, a
 * sub-line and a chevron (`MEM-007` AC-6).
 *
 * `href: null` marks a row whose destination is not built. **There is no longer one.** Tickets &
 * orders carried `href: null` and `notBuilt: 'MEM-022'` from `MEM-007` until `MEM-022` landed, and
 * it is now the entry point AC-3 requires — *"Tickets & orders is reached from the account page"*.
 * The `notBuilt` shape stays in the type and in `member-identity-screens.test.ts`, because it is
 * how the next unbuilt destination gets named rather than drawn as a dead chevron, and the parity
 * report's **Q4** is about the pattern rather than about this row.
 */
export interface MemberAccountSettingsRow {
  /**
   * The row's control id, which is also its key into {@link MEMBER_ACCOUNT_CONTROL_READS}.
   *
   * Typed rather than a bare `string` (`MEM-049` AC-3): a row added here without an entry in that
   * table is a `tsc` error, so the next settings row cannot arrive unclassified the way these four
   * did.
   */
  readonly id: MemberAccountControlId;
  readonly label: string;
  /** `null` for a destination that is not built. See {@link MemberAccountSettingsRow.notBuilt}. */
  readonly href: string | null;
  /**
   * The story that will deliver this row, when {@link MemberAccountSettingsRow.href} is `null`.
   *
   * **No row carries it today**, and the field stays because the alternative is worse: without it
   * the array's inferred type has no `notBuilt` at all, `'notBuilt' in row` narrows to `never`,
   * and the next person to add an unbuilt row has to re-derive the whole not-built treatment from
   * scratch. `MEM-007` AC-6 established that a destination which does not exist is *named* rather
   * than drawn as a dead chevron, and this is where that decision lives.
   */
  readonly notBuilt?: string;
}

export const MEMBER_ACCOUNT_SETTINGS_ROWS: ReadonlyArray<MemberAccountSettingsRow> = [
  {
    id: 'password',
    label: 'Change password',
    href: MEMBER_PROFILE_ROUTES.changePassword,
  },
  /**
   * **`Web App.dc.html` §2.15's own row labels**, adopted 2026-09-12.
   *
   * They read *Privacy* and *Social channels* — this app's names for the screens they open —
   * against the prototype's *Privacy & opt-ins* and *Social Accounts & Channels*. Presentation is
   * the prototype's (`docs/standards/building-from-designs.md`), no criterion quotes either
   * string, and the longer labels say more about what is behind the chevron: *Privacy* alone does
   * not suggest the notification election that is on that screen (`MEM-010` AC-6).
   *
   * The **screens** keep their own headings — `MEMBER_PRIVACY_COPY.title` is still *Privacy* — for
   * the same reason the prototype does: a row is a description of a destination and a heading is
   * its name.
   */
  {
    id: 'privacy',
    label: 'Privacy & opt-ins',
    href: MEMBER_PROFILE_ROUTES.privacy,
  },
  {
    id: 'matching-preferences',
    label: 'Matching preferences',
    href: MEMBER_PROFILE_ROUTES.matchingPreferences,
  },
  {
    id: 'social-channels',
    label: 'Social Accounts & Channels',
    href: MEMBER_PROFILE_ROUTES.socialChannels,
  },
  {
    id: 'tickets',
    label: 'Tickets & orders',
    href: MEMBER_PROFILE_ROUTES.ticketsAndOrders,
  },
];

/**
 * The rows this account may actually use (`MEM-049` AC-1, AC-2).
 *
 * A filter over the one list rather than a second list, so the rows a member with a profile sees
 * and the rows an unfinished account sees cannot drift apart — and so the order is the prototype's
 * in both cases. Nothing here names a row: which rows survive is entirely
 * {@link memberAccountControlWorksWithoutProfile}'s answer, which is entirely `contracts`' answer,
 * which is entirely `profile-service.ts`'s answer.
 */
export function memberAccountSettingsRowsFor(
  hasProfile: boolean,
): ReadonlyArray<MemberAccountSettingsRow> {
  if (hasProfile) return MEMBER_ACCOUNT_SETTINGS_ROWS;
  return MEMBER_ACCOUNT_SETTINGS_ROWS.filter((row) =>
    memberAccountControlWorksWithoutProfile(row.id),
  );
}

/**
 * The password strength meter's four segments (the prototype's CHANGE PASSWORD screen, and the
 * parity report's item 9 — "cheap", and it was).
 *
 * Scored on the three things `describePasswordFailure` actually enforces plus length, so the meter
 * cannot tell a member they are fine while the policy refuses them. Pure, and exported, because it
 * is the one piece of logic on that screen worth asserting directly.
 */
export function passwordStrengthSegments(password: string): number {
  if (password.length === 0) return 0;
  let filled = 0;
  if (password.length >= 8) filled += 1;
  if (/[0-9]/u.test(password)) filled += 1;
  if (/[^A-Za-z0-9\s]/u.test(password)) filled += 1;
  if (password.length >= 12) filled += 1;
  return filled;
}

/** The rule, stated once, beside the meter. The prototype's own sentence. */
export const PASSWORD_RULE_HINT = 'At least 8 characters, one number and one symbol';

/**
 * Interest selection state for the chips (`MEM-009` AC-2).
 *
 * Pure so the "pick 3–5" behaviour is assertable without rendering: at the maximum, unselected
 * chips are disabled rather than hidden, because a member needs to see what they did not pick.
 */
export function interestChipState(
  selected: ReadonlyArray<string>,
  termId: string,
  bounds: { readonly min: number; readonly max: number },
): { readonly active: boolean; readonly disabled: boolean } {
  const active = selected.includes(termId);
  return { active, disabled: !active && selected.length >= bounds.max };
}

/**
 * The optional matching answers once a save has landed: the saved patch over what was loaded, a
 * `null` erasing its attribute (TW 44260267).
 *
 * The save returns no answers, so this is what "Improve your matches" reseeds from. Reseeding from
 * the answers loaded before the save put the old Industry back on screen until a reload.
 */
export function answersAfterSave(
  current: Readonly<Record<string, string>>,
  patch: Readonly<Record<string, string | null>>,
): Record<string, string> {
  const next: Record<string, string> = { ...current };
  for (const [attribute, value] of Object.entries(patch)) {
    if (value === null) delete next[attribute];
    else next[attribute] = value;
  }
  return next;
}

/**
 * The chips a multi-select group draws: the member's own retired picks that are still selected,
 * then the terms on offer (TW 44260222).
 *
 * Retiring keeps a member's picks (`ADM-002` AC-4) but takes them off offer, so drawing only the
 * offered terms left picks the "N/5 chosen" count included and nobody could see or remove. They go
 * first, so a member who opens the screen sees what they already hold. Once deselected one is
 * gone: a retired term is never offered again.
 */
export function chipTermsWithOwnRetiredPicks<Term extends { readonly termId: string }>(
  offered: ReadonlyArray<Term>,
  ownRetired: ReadonlyArray<Term>,
  selected: ReadonlyArray<string>,
): ReadonlyArray<Term> {
  const onOffer = new Set(offered.map((term) => term.termId));
  return [
    ...ownRetired.filter((term) => selected.includes(term.termId) && !onOffer.has(term.termId)),
    ...offered,
  ];
}

/** Whether the preferences screen may be saved — `MEM-009` AC-2's floor and AC-6's "exactly one". */
export function matchingPreferencesAreSavable(draft: {
  readonly interestTagTermIds: ReadonlyArray<string>;
  readonly connectionIntentTermIds: ReadonlyArray<string>;
  readonly connectionFormatTermId: string;
  readonly currentGoal?: string;
}): boolean {
  return (
    draft.interestTagTermIds.length >= 3 &&
    draft.interestTagTermIds.length <= 5 &&
    draft.connectionIntentTermIds.length >= 1 &&
    draft.connectionFormatTermId.length > 0 &&
    (draft.currentGoal ?? '').length <= 500
  );
}

/**
 * Shown when step 1 completed but no session could be granted (`MEM-002` AC-6).
 *
 * **Every word here is chosen not to answer the question it raises.** The member may have an
 * account under this address; they may have mistyped a password; there may be no account at all
 * and the directory simply declined. The sentence is true in all three cases, and it says what to
 * do next in each of them.
 *
 * Saying "that address is already registered" would be the enumeration leak AC-6 exists to
 * prevent — and it would be a leak *worth having* only if it helped, which it does not: the
 * member's next move is the same either way.
 */
export const REGISTRATION_NEEDS_SIGN_IN_TITLE = 'Sign in to carry on';

export const REGISTRATION_NEEDS_SIGN_IN_BODY =
  'We could not start a session for that address. If you already have an H2HSpark account — as a ' +
  'member or an organizer — sign in and your registration will pick up where it left off.';

export const REGISTRATION_NEEDS_SIGN_IN_ACTION = 'Sign in';

/**
 * Shown when step 1 refused because the address already has an account (`MEM-002` AC-6, ruled
 * 2026-09-02).
 *
 * **This is the screen the ruling is about.** The same person used to be shown *"We could not save
 * that just now, and nothing was lost — press Continue to try again"*, and Continue failed the same
 * way every time. Nothing about that sentence was false; it was simply not the answer, and there
 * was no way out of it.
 *
 * ## What it says and what it still refuses to say
 *
 * The body is the API's own sentence — `IdentityAlreadyExistsError`'s default, which the server
 * sends verbatim — kept here rather than rendered from the response, so a wording change on either
 * side is a visible diff instead of a silent one. It names the **situation** and the **way
 * forward** and stops there.
 *
 * It does not name the account's state: whether it is verified, suspended, locked or brand new is
 * not said and is not knowable from this screen, which is the half of AC-6 the reword left
 * untouched. And the address it echoes is `REGISTRATION_ADDRESS_ALREADY_TAKEN_ADDRESS_LABEL` over
 * the value the caller just typed into this form — they knew it before the screen rendered, so
 * showing it back discloses nothing and makes a typo obvious.
 *
 * **The relaxation is this screen only.** `/app/login`, password recovery and the lockout answer
 * a known and an unknown address identically and must go on doing so — `USR-010` owns them, and
 * revealing an account *there* is what tells somebody which addresses are worth spending attempts
 * on. Nothing in this file is rendered by any of them.
 */
export const REGISTRATION_ADDRESS_ALREADY_TAKEN_TITLE = 'That address already has an account';

export const REGISTRATION_ADDRESS_ALREADY_TAKEN_BODY =
  'An account with that email already exists. Try signing in instead — your registration will ' +
  'pick up where it left off.';

/** The label over the address the caller typed. The only address this screen ever shows. */
export const REGISTRATION_ADDRESS_ALREADY_TAKEN_ADDRESS_LABEL = 'You entered';

export const REGISTRATION_ADDRESS_ALREADY_TAKEN_ACTION = 'Sign in';

/* ============================================================================
   Logging in from sign-up (`MEM-001` AC-9, Sumit 2026-09-28, TW 44216814)
   ============================================================================ */

/**
 * **Step 1's way in for somebody who already has an account.**
 *
 * Reserve My Spot pressed while signed out opens sign-up, and since the event page's own sign-in
 * line went (PR #607) this is the only road an existing member has to the reservation. Step 1 had
 * no log-in link, so they had to fill in the form to be told their address was taken. The wording
 * follows the organizer sign-up's *"Already have an account? Log in instead"*, and the header's
 * *Log in*. A departure from `Web App.dc.html`, whose reservation-flow account step draws no such
 * link; recorded in `MEM-001`'s notes.
 */
export const REGISTRATION_LOG_IN_PROMPT = 'Already have an account?';

export const REGISTRATION_LOG_IN_ACTION = 'Log in';

/**
 * Where signing in from sign-up goes: the login page, told to come back to where sign-up was
 * going to send the member (`returnTo`: an event's checkout, when Reserve My Spot opened it).
 *
 * The address-already-has-an-account screen used to send `next=/app/join`, which the login page
 * strips to `/app` (it never returns anybody to an auth screen), so the event was lost there.
 * Only a path on this site is passed on; anything else is the login page's own default, `/app`.
 */
export function registrationSignInPath(returnTo: string | null | undefined): string {
  const onThisSite =
    typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//');
  return onThisSite ? `/app/login?next=${encodeURIComponent(returnTo)}` : '/app/login';
}

/* ============================================================================
   A visitor who already has an account (`MEM-002` AC-7, `MEM-001` AC-7)
   ============================================================================ */

/**
 * Step 1's heading for somebody who is already signed in.
 *
 * The client's ruling: *an existing account should be shown as logged in, and should finish member
 * registration by filling in the profile questions.* So an organizer who opens `/app/join` is told
 * which account this is about to become a member — and is not offered a second one.
 *
 * **This discloses nothing under `MEM-002` AC-6.** The address shown is the address on the session
 * the reader is already holding; a person can only be told this about themselves, and they knew it
 * before the page rendered.
 */
export const REGISTRATION_SIGNED_IN_LABEL = 'Signed in as';

/** Why the account half of step 1 is not on screen. One sentence, under the address. */
export const REGISTRATION_SIGNED_IN_NOTE =
  'You already have an H2HSpark account, so there is nothing to create — just tell us a little ' +
  'about your work and carry on.';

/* ============================================================================
   Sign-up, pressed by a member who has already finished it
   (`MEM-001` AC-8, Sumit 2026-09-21, TW 44145257)
   ============================================================================ */

/**
 * **What sign-up says to a member whose registration is already complete.**
 *
 * The report (TW 44145257) is *"Sign up / join link does not work. Page just refreshes"*. It
 * worked: `MEM-001` AC-5 resumes an account where it stopped, and an account that stopped nowhere
 * is sent on to where it was going. Pressed from an app page, that lands on the page you were
 * already looking at, which is indistinguishable from a refresh — and pressed from anywhere, it
 * is an action that appears to do nothing.
 *
 * Sumit, 2026-09-21: *"Sign-up will also say you already have an account, instead of quietly
 * moving you on."* So the wizard says whose account it is and offers the way in, and the member
 * decides. **Nothing is taken away**: the destination the silent redirect would have used is what
 * the action goes to, so the one extra press is the whole cost.
 *
 * The header the same decision changes (`MEM-043` AC-8) is the other half of this report: the two
 * account actions are not offered to a signed-in member at all, so this screen is what is left —
 * an old link, a typed address, a bookmark.
 *
 * **It discloses nothing.** The name is the name on the session the reader is holding, exactly as
 * `REGISTRATION_SIGNED_IN_LABEL` above, and for the same reason.
 */
export const REGISTRATION_ALREADY_A_MEMBER_TITLE = "You're already signed in";

/** With the member's own name, when the session carries one. */
export function registrationAlreadyAMemberTitle(displayName: string | undefined): string {
  const name = (displayName ?? '').trim();
  return name === ''
    ? REGISTRATION_ALREADY_A_MEMBER_TITLE
    : `${REGISTRATION_ALREADY_A_MEMBER_TITLE} as ${name}`;
}

export const REGISTRATION_ALREADY_A_MEMBER_BODY =
  'Your H2HSpark account is set up, so there is nothing left to sign up for. Carry on into the ' +
  'app — or log out first if you meant to join with a different address.';

export const REGISTRATION_ALREADY_A_MEMBER_ACTION = 'Go to the app';

/**
 * What the wizard says when Continue could not do what it was pressed for.
 *
 * ## The defect this replaces
 *
 * `onContinue` was called as `void onContinue()` with no catch. Every rejection in the whole flow
 * — the step-1 call, the per-step save, completion — was therefore *invisible*: the button
 * appeared to do nothing at all, which teaches a person that what they typed was wrong and sends
 * them to re-edit fields that were fine. A silent failure is worse than the specific bug that
 * caused this one, because it hides the next one too.
 *
 * ## Why the two branches
 *
 * Duck-typed on `ApiRequestError`'s `statusCode` rather than by importing it, for the reason
 * `member-in-event/constants.ts` gives about the same check: this module is pure and is loaded
 * directly by `tsx --test`, and importing `@h2hspark/ui` would pull a React component tree in
 * behind one property test. A thrown value carrying a numeric `statusCode` came *from* a
 * response; a `TypeError` out of `fetch` did not — and "check your connection" is untrue advice
 * for somebody whose connection is fine.
 *
 * ## And why there is now a third branch that is not a sentence
 *
 * These two are for failures worth pressing Continue against. *"The address already has an
 * account"* is not one: pressing again produces the identical refusal, which is the loop reported
 * on 2026-09-02 — a member met `We could not save that just now` for ever, on a screen that could
 * simply have told them where to go. `MEM-002` AC-6 was reworded that day to say the situation
 * and the way forward, so {@link describeRegistrationFailure} answers with a *kind* rather than a
 * string and the wizard renders the sign-in route for that one.
 *
 * Neither sentence below names an account, an address or a state. That has not changed: what step
 * 1 may now name is that the address is **taken**, and it names it on its own screen, not in a
 * "could not save" line. The account's state — verified, suspended, locked — is still nobody's
 * business here, and the only address ever shown is the one the caller just typed into this form.
 */
export const REGISTRATION_CONTINUE_REFUSED =
  'We could not save that just now, and nothing was lost — press Continue to try again.';

export const REGISTRATION_CONTINUE_UNREACHABLE =
  'We could not reach H2HSpark, and nothing was lost — check your connection and press Continue ' +
  'again.';

/**
 * What a failed press was, once the wizard has decided what to do about it.
 *
 * Two kinds rather than a string, because they belong on different screens. A `retry` is a
 * sentence above the Continue button; `address-already-has-an-account` is not a failure to retry
 * at all — pressing Continue again produces the identical refusal for ever, which is precisely the
 * loop reported on 2026-09-02 — so it takes over the view and offers the way out.
 */
export type RegistrationFailure =
  | { readonly kind: 'retry'; readonly message: string }
  | { readonly kind: 'address-already-has-an-account' }
  /**
   * The server refused what was sent as invalid (`400`, `MEM-059` AC-3). Pressing again sends the
   * identical body and meets the identical refusal, so this is never the retry sentence: the
   * wizard says to check what was entered, on the field where it can tell which one (`field`),
   * and above Continue where it cannot (`null`).
   */
  | { readonly kind: 'invalid'; readonly field: 'email' | null };

/**
 * What the email field says when the address is not one the server would accept (`MEM-059`
 * AC-1, AC-3). The wording is the story's, settled 2026-10-22.
 */
export const REGISTRATION_EMAIL_INCOMPLETE = "Check your email address. It doesn't look complete.";

/**
 * Step 1's address, checked with the rule the server applies before anything is sent (`MEM-059`
 * AC-1, AC-2).
 *
 * **The server's rule, not a copy of it.** `emailAddressField` is what step 1's contract parses
 * with (`USR-015`), so an address this accepts is one the server accepts and an address this
 * refuses is one the server would have refused with a 400. It trims and lower-cases on the way,
 * which is why the accepted address is returned and sent rather than what was typed: the spaces
 * around a pasted address are not part of it.
 */
export function checkStepOneEmailAddress(
  typed: string,
): { readonly ok: true; readonly address: string } | { readonly ok: false } {
  const parsed = emailAddressField.safeParse(typed);
  return parsed.success ? { ok: true, address: parsed.data } : { ok: false };
}

/** The API's own code for it. Compared as a value; nothing here reads the server's wording. */
const ADDRESS_ALREADY_HAS_AN_ACCOUNT = 'AUTH_IDENTITY_ALREADY_EXISTS';

/**
 * Whether an answered request was step 1 saying the address is taken (`MEM-002` AC-6).
 *
 * Reads `responseBody` off `ApiRequestError` by duck-typing, for the reason the header above
 * gives about `statusCode`: this module is pure and is loaded directly by `tsx --test`, and
 * importing `@h2hspark/ui` would pull a React component tree in behind one property test.
 *
 * The **code** is what is matched, never the message. A client that branched on the sentence
 * would break the next time the sentence was improved, and the sentence is the one thing about
 * this response that is meant to be free to change.
 */
function isAddressAlreadyRegistered(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const answered = error as { statusCode?: unknown; responseBody?: unknown };
  if (answered.statusCode !== 409) return false;
  if (typeof answered.responseBody !== 'string') return false;
  try {
    const parsed: unknown = JSON.parse(answered.responseBody);
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { errorCode?: unknown }).errorCode === ADDRESS_ALREADY_HAS_AN_ACCOUNT
    );
  } catch {
    return false;
  }
}

/**
 * The field a `400` is about, where the refusal says (`MEM-059` AC-3).
 *
 * The API answers a refused body with zod's first reason and no field path — *"Invalid email"* —
 * so the only evidence is the reason's wording. The address is the one field whose reasons name
 * it, and the one the production refusals were about; anything else is `null`, and the wizard
 * says so above Continue rather than guessing a field.
 */
function fieldTheRefusalIsAbout(responseBody: unknown): 'email' | null {
  if (typeof responseBody !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(responseBody);
    const message =
      typeof parsed === 'object' && parsed !== null ? (parsed as { message?: unknown }).message : undefined;
    return typeof message === 'string' && /\bemail\b/iu.test(message) ? 'email' : null;
  } catch {
    return null;
  }
}

export function describeRegistrationFailure(error: unknown): RegistrationFailure {
  if (isAddressAlreadyRegistered(error)) return { kind: 'address-already-has-an-account' };
  if (typeof error === 'object' && error !== null && (error as { statusCode?: unknown }).statusCode === 400) {
    return { kind: 'invalid', field: fieldTheRefusalIsAbout((error as { responseBody?: unknown }).responseBody) };
  }
  const answered =
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { statusCode?: unknown }).statusCode === 'number';
  return {
    kind: 'retry',
    message: answered ? REGISTRATION_CONTINUE_REFUSED : REGISTRATION_CONTINUE_UNREACHABLE,
  };
}

/**
 * A member's name, split into the two fields step 1 asks for.
 *
 * The account carries one `displayName`; registration carries a first and a last name. Everything
 * after the first space is the last name, so "Ada Lovelace King" keeps "Lovelace King" rather than
 * dropping a word — and a single-word display name prefills the first name alone rather than
 * inventing a surname the member would then have to delete.
 *
 * Prefilling rather than re-asking is the point: identity the platform already holds should not be
 * typed twice, and a wizard that asked an organizer for their own name would be admitting it did
 * not know who was signed in.
 */
export function splitDisplayName(displayName: string): {
  readonly firstName: string;
  readonly lastName: string;
} {
  const parts = displayName.trim().split(/\s+/u).filter((part) => part.length > 0);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/**
 * The registration wizard's way out (TW 44131717, Sumit 2026-09-18): back to where the person came
 * from, or home. The prototype's own word on its reservation funnel's bar (§3.1).
 */
export const REGISTRATION_CANCEL_LABEL = 'Cancel';

/**
 * The password rule, under step 1's password field (TW 44132998) — the sentence the step has always
 * used, moved rather than reworded.
 */
export const REGISTRATION_PASSWORD_RULE = 'At least 8 characters, with a number and a symbol.';
